import * as echarts from "echarts";
import type { ChartConfig } from "@bi-studio/shared/lib";
import type { ChartData, ChartSize } from "./types";
import {
   getChartInstance,
   setupResize,
   clearContainer,
   resolveColors,
} from "./utils";

export const renderBarChart = (
   container: HTMLElement | null,
   data: ChartData,
   config: ChartConfig,
   _size?: ChartSize,
   theme: 'light' | 'dark' = 'light'
): HTMLElement | null => {
   if (!container || typeof window === "undefined") return null;

   clearContainer(container);

   // Set container size
   container.style.width = "100%";
   container.style.height = "100%";

   const chartInstance = getChartInstance(container, theme);
   const xValues = data.map((row) => String(row[config.xField ?? ""] ?? ""));

   const isHorizontal = config.orientation === "horizontal";
   const colors = resolveColors(config, theme);

   // Prepare data for ECharts
   const seriesData = data.map((row) => ({
      value: Number(row[config.yField ?? ""]) || 0,
      name: String(row[config.xField ?? ""] ?? ""),
   }));

   const option: echarts.EChartsOption = {
      backgroundColor: 'transparent',
      tooltip: {
         trigger: "axis",
         axisPointer: {
            type: "shadow",
         },
      },
      grid: {
         left: "10%",
         right: "10%",
         bottom: "15%",
         top: "10%",
         containLabel: true,
      },
      xAxis: isHorizontal
         ? {
            type: "value",
            boundaryGap: [0, 0.1],
            axisLabel: {
            },
         }
         : {
            type: "category",
            data: xValues,
            axisLabel: {
               interval: 0,
               rotate: 45,
            },
         },
      yAxis: isHorizontal
         ? {
            type: "category",
            data: xValues,
            axisLabel: {
            },
         }
         : {
            type: "value",
            boundaryGap: [0, 0.1],
            show: false,
            axisLabel: {
            },
         },
      series: [
         {
            name: config.yField || "Value",
            type: "bar",
            data: seriesData.map((item) => item.value),
            itemStyle: {
               color: (params: any) => {
                  const val = params.value;
                  const rules = (config.conditionalFormatting as any[]) || [];

                  for (const rule of rules) {
                     let match = false;
                     const threshold = rule.threshold;
                     switch (rule.operator) {
                        case '>': match = val > threshold; break;
                        case '>=': match = val >= threshold; break;
                        case '<': match = val < threshold; break;
                        case '<=': match = val <= threshold; break;
                        case '==': match = val == threshold; break;
                     }

                     if (match && rule.backgroundColor) {
                        return rule.backgroundColor;
                     }
                  }

                   return colors[0];
               },
            },
            label: {
               show: true,
            },
         },
      ],
   };

   chartInstance.setOption(option);
   setupResize(container, chartInstance);

   return container;
};
